import { routes } from "../../app/routes";
import { SubscriptionItemModel}  from "../../data/entities";
import {useCRUDHandlers, 
    useGridActions, 
    useSetPageTitle,
    useSharedState,
    useUrlParams,
} from "../../hooks";
import { SubscriptionItemService } from "../../services";
import { Breakpoints, Button, IconAndText, IconDefinitions, ODataGrid, ResponsiveGridColumn, SearchableYearFilterDropdown, TitlelessCard } from "../../components";
import { GridCellProps, MenuItem } from "@progress/kendo-react-all";
import { GridFormattedDateCell, ViewSubscriptionInvoiceModal } from "./components";
import { useCallback, useEffect, useState } from "react";
import SearchableMonthFilterDropdown from "../../components/SearchableDropdown/SearchableMonthFilterDropdown";
import { usePageActions } from "../PageLayout";
import { Col, Row } from "react-bootstrap";


export function SubscriptionItemsPage() {  
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const subscriptionId = parseInt(useUrlParams("id"));
  const gridData = useSharedState<Array<SubscriptionItemModel>>([]);
  const subscriptionItemService = new SubscriptionItemService();
  const {onSelect, showAddState, showInvoiceState, showInvoice, itemInvoiceState } = useGridActions<SubscriptionItemModel>(routes.host.subscription.history);
  const { gridRef, getItems } = useCRUDHandlers<SubscriptionItemModel>(subscriptionItemService, "SubscriptionItem", showAddState);

  const handleClearFilters = () => {
    setSelectedYear(null);  
    setSelectedMonth(null); 
  };

  useSetPageTitle("Billing History", []);

  usePageActions(
    <Button onClick={handleClearFilters}>
      <IconAndText iconName={IconDefinitions.refresh} text="Clear Filters" />
    </Button>
  )

  const fetchGridData = async (filterQuery: string) => {
    try {
      const response = await getItems(filterQuery);
      if (response && response.value) {
        gridData.setter(response.value); 
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    const filterQuery = generateFilterQuery();
    fetchGridData(filterQuery);
    // eslint-disable-next-line
  }, [selectedYear, selectedMonth, subscriptionId]);

  const generateFilterQuery = () => {
    let filterQuery = `filter=subscriptionId eq ${subscriptionId}`;

    if (selectedYear) {
      filterQuery += ` and year(billingDate) eq ${selectedYear}`;
    }

    if (selectedMonth !== null) {
      const month = parseInt(selectedMonth.toString(), 10); 
      filterQuery += ` and month(billingDate) eq ${month + 1}`;
    }

    filterQuery += "&$orderby=billingDate desc&$expand=subscription";
    return filterQuery;
  };

  const commandsCell = useCallback(
    (props: GridCellProps) => (
      <>
        <td className="text-center">
          <Button
            onClick={() => {
              showInvoiceState.setter(true);
              showInvoice(props.dataItem);
            }}
          >
            <IconAndText iconName={IconDefinitions.envelopeOpenText} text="View Invoice" />
          </Button>
        </td>
      </>
    ),
    // eslint-disable-next-line
    [showInvoice]
  );
  
  return (
    <TitlelessCard bodyClassName="p-0">
      <Row className="mb-3">
        <Col>
          <div>
            <SearchableYearFilterDropdown  
              selectedYear={selectedYear} 
              onYearChange={setSelectedYear}
            />
          </div>
        </Col>
        <Col>
          <div>
            <SearchableMonthFilterDropdown 
              selectedMonth={selectedMonth} 
              onMonthChange={setSelectedMonth}
            />
          </div>
        </Col>
      </Row>
      <ODataGrid
        ref={gridRef}
        getData={() => getItems(generateFilterQuery())}
        dataState={gridData}
        onRowMenuSelect={onSelect}
        sort={[{ field: "billingDate", dir: "desc" }]}
        scrollable="scrollable"
        pageSize={10}
      >
        <ResponsiveGridColumn
          title="Billing Date"
          field="billingDate"
          sortable={true}
          cell={(props) => <GridFormattedDateCell {...props} field="billingDate"/>}  
        />
        <ResponsiveGridColumn
          title="Subscription Name"
          field="subscription.name"
          sortable={true}
        />
        <ResponsiveGridColumn
          className="column-url"
          field="url"
          title="Invoice"
          sortable={false}
          filterable={false}
          headerCell={() => null}
          cell={commandsCell}
          width = {160}
          breakpoint={Breakpoints.sm}
        />
        <MenuItem
          text="View Invoice"
          data={{ action: "invoice" }}
          icon={IconDefinitions.envelopeOpenText}
        />
      </ODataGrid>

      <ViewSubscriptionInvoiceModal
        showInvoiceState={showInvoiceState}
        showState={showInvoiceState}
        subscriptionItemId={itemInvoiceState.value.id}
      />
      </TitlelessCard>
  );
}

export default SubscriptionItemsPage;
